import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GameFrame, BetControls } from "@/components/GameFrame";
import { useAudio } from "@/context/GameFeel";

const SYM = { cherry: "🍒", lemon: "🍋", bell: "🔔", star: "⭐", seven: "7️⃣", diamond: "💎" };
const ALL = ["cherry", "lemon", "bell", "star", "seven", "diamond"];
const WEDGES = [0, 1, 0, 2, 0.5, 3, 0, 1.5, 0.5, 5, 0, 2, 0.5, 1.5, 0, 8];
const COLORS = ["#1f2937", "#F59E0B", "#1f2937", "#8B5CF6", "#334155", "#EC4899", "#1f2937", "#F59E0B", "#334155", "#10B981", "#1f2937", "#8B5CF6", "#334155", "#F59E0B", "#1f2937", "#06B6D4"];

export default function WheelSlotsGame() {
  const { user, setUser } = useAuth();
  const { react, sfx } = useAudio();
  const [bet, setBet] = useState(100);
  const [reels, setReels] = useState(["seven", "diamond", "seven"]);
  const [rot, setRot] = useState(0);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const seg = 360 / WEDGES.length;

  const spin = async () => {
    if (bet > (user?.coins ?? 0)) return toast.error("Not enough coins");
    setBusy(true); setResult(null);
    const iv = setInterval(() => setReels([rand(), rand(), rand()]), 80);
    try {
      const { data } = await api.post("/hybrid/wheelslots/spin", { bet });
      const finalRot = 360 * 6 + (360 - data.wheel_index * seg - seg / 2);
      setRot((p) => p - (p % 360) + finalRot);
      const ticks = setInterval(() => sfx("tick"), 200);
      await new Promise((r) => setTimeout(r, 1000));
      clearInterval(iv);
      setReels(data.reels);
      await new Promise((r) => setTimeout(r, 3200));
      clearInterval(ticks);
      setResult(data);
      setUser((u) => ({ ...u, coins: data.coins }));
      if (data.payout > 0) toast.success(`${data.combined}x · +${data.payout.toLocaleString()} coins${data.combo_bonus > 1 ? " · COMBO!" : ""}`);
      else toast("No win — spin again!");
      react({ outcome: data.payout > 0 ? "win" : "loss", game: "wheelslots", big: data.combo_bonus > 1 || data.combined >= 10 });
    } catch (e) { clearInterval(iv); toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };
  const rand = () => ALL[Math.floor(Math.random() * ALL.length)];
  const gradient = WEDGES.map((_, i) => `${COLORS[i]} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ");

  return (
    <GameFrame title="Wheel Slots" subtitle="PhonePlay signature — reels + wheel stack into one win"
      side={<>
        <BetControls bet={bet} setBet={setBet} coins={user?.coins} disabled={busy} onPlay={spin} playLabel={busy ? "Spinning…" : "SPIN BOTH"} testid="wheelslots-spin" />
        <div className="glass rounded-2xl p-5 border border-white/10 text-sm text-gray-400">
          <p className="font-semibold text-white mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /> How it blends</p>
          Reels give a base multiplier, the wheel adds a bonus, and hitting <span className="text-amber-300">both</span> triggers a 1.5× combo. Coins only.
        </div>
      </>}>
      <div className="glass rounded-3xl p-8 border border-amber-500/20 glow-coins flex flex-col items-center gap-6">
        <div className="flex gap-3">
          {reels.map((s, i) => (
            <motion.div key={i} animate={busy ? { y: [0, -8, 0] } : {}} transition={{ repeat: busy ? Infinity : 0, duration: 0.2 }}
              className="w-20 h-24 sm:w-24 sm:h-28 rounded-2xl bg-black/50 border-2 border-amber-500/30 flex items-center justify-center text-5xl">{SYM[s]}</motion.div>
          ))}
        </div>
        <div className="relative">
          <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-20 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[16px] border-l-transparent border-r-transparent border-t-amber-400" />
          <motion.div animate={{ rotate: rot }} transition={{ duration: 3.2, ease: [0.16, 1, 0.3, 1] }}
            className="w-56 h-56 rounded-full border-4 border-amber-500/40 relative" style={{ background: `conic-gradient(${gradient})` }}>
            {WEDGES.map((m, i) => (
              <div key={i} className="absolute inset-0 flex items-start justify-center" style={{ transform: `rotate(${i * seg + seg / 2}deg)` }}>
                <span className="mt-2 text-[10px] font-mono font-bold text-white/90">{m}x</span>
              </div>
            ))}
            <div className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black border-2 border-amber-500/50" />
          </motion.div>
        </div>
        {result && (
          <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className={`text-center px-6 py-3 rounded-xl ${result.payout > 0 ? "bg-emerald-500/15 text-emerald-300" : "bg-white/5 text-gray-400"}`}>
            {result.payout > 0 ? `Slots ${result.slot_mult}x + Wheel ${result.wheel_mult}x = ${result.combined}x → +${result.payout.toLocaleString()}` : "No win this time"}
          </motion.div>
        )}
      </div>
    </GameFrame>
  );
}
